import { Box, Stack, Typography } from '@mui/material'

import { useTranslate } from '@tolgee/react'

import { observer } from 'mobx-react-lite'
import { FC } from 'react'

import { DamagedIcon, DangerousIcon, ReeferIcon, useMinimalsTheme } from '@tom-ui/ui'
import { computed } from 'mobx'
import { isContainerReefer } from '../../utils/container.utils'
import { formatDateTime } from '../../utils/date.utils'
import { CarrierTypeIcon } from './CarrierTypeIcon'
import { ContainerData, OrderData } from './ContainerJourneyCard'
import { ContainerJourneyCardChip } from './ContainerJourneyCardChip'

type Props = {
  direction: 'Inbound' | 'Outbound'
  containerData?: ContainerData
  orderData?: OrderData
}

const VisitHeader: FC<Props> = props => {
  const { direction, orderData } = props
  const { t } = useTranslate()

  const carrierVisit = orderData?.carrierVisit

  return (
    <>
      <Typography variant='body1' data-cy={`${direction}-carrier-visit-identifier`}>
        {carrierVisit?.identifier ?? t(orderData?.carrierVisitType ?? 'unknown')}
      </Typography>
      <CarrierTypeIcon type={orderData?.carrierVisitType} />
    </>
  )
}

export const ContainerJourneyCardCarrierVisitInfo: FC<Props> = observer(props => {
  const { containerData, orderData, direction } = props
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const isReefer = computed(() => isContainerReefer(containerData?.type)).get()

  const carrierVisit = orderData?.carrierVisit

  const displayVisitDateTime = () => {
    if (direction === 'Inbound') {
      if (carrierVisit?.ata) return `${t('ata', 'ATA')}: ${formatDateTime(carrierVisit?.ata)}`
      else if (carrierVisit?.eta) return `${t('eta', 'ETA')}: ${formatDateTime(carrierVisit?.eta)}`
      else if (orderData?.carrierVisitType) return t('awaitingDropOff', 'Awaiting drop off')
    } else if (direction === 'Outbound') {
      if (carrierVisit?.atd) return `${t('atd', 'ATD')}: ${formatDateTime(carrierVisit?.atd)}`
      else if (carrierVisit?.etd) return `${t('etd', 'ETD')}: ${formatDateTime(carrierVisit?.etd)}`
      else if (orderData?.carrierVisitType) return t('awaitingPickUp', 'Awaiting pick up')
    }

    return ''
  }

  return (
    <Box>
      <Stack direction={'row'} spacing={1}>
        <VisitHeader {...props} />
      </Stack>
      <Typography variant='body2'>{displayVisitDateTime()}</Typography>

      {orderData?.carrierVisitType === 'Train' && orderData?.railcarName && (
        <Typography variant='body2'>
          {t('railcarNo', 'Railcar no')}: {orderData?.railcarName}
        </Typography>
      )}

      <Stack direction='row' mt={theme.customSpacing.xxs} maxWidth={'100%'}>
        <Stack direction={'row'} gap={theme.customSpacing.xxs} flexWrap='wrap'>
          {orderData && <ContainerJourneyCardChip label={(orderData?.isEmpty && 'E') || 'F'} />}
          {isReefer && (
            <ContainerJourneyCardChip
              label={
                <Typography
                  display={'flex'}
                  gap={'4px'}
                  fontSize={'14px'}
                  sx={{ alignItems: 'center' }}
                  component={'div'}
                  variant='body2'
                >
                  <ReeferIcon sx={{ height: '16px', width: '16px' }} />
                </Typography>
              }
            />
          )}
          {orderData?.imoClasses && orderData.imoClasses.length > 0 && (
            <ContainerJourneyCardChip
              label={
                <Typography
                  display={'flex'}
                  gap={'4px'}
                  fontSize={'14px'}
                  sx={{ alignItems: 'center' }}
                  component={'div'}
                  variant='body2'
                >
                  <DangerousIcon sx={{ height: '16px', width: '16px', marginRight: '4px' }} />
                  {orderData?.imoClasses.join(', ')}
                </Typography>
              }
            />
          )}
          {containerData?.damages.find(d => d.status === 'Active') && (
            <ContainerJourneyCardChip
              label={
                <Typography
                  display={'flex'}
                  gap={'4px'}
                  fontSize={'14px'}
                  sx={{ alignItems: 'center' }}
                  component={'div'}
                  variant='body2'
                >
                  <DamagedIcon sx={{ height: '16px', width: '16px' }} />
                </Typography>
              }
            />
          )}
          {!!orderData?.grossWeight && (
            <ContainerJourneyCardChip
              label={`${t('gwt', 'GWT')}: ${orderData?.grossWeight.toLocaleString(undefined, { maximumFractionDigits: 0 })}kg`}
            />
          )}
          {!!orderData?.vgm && (
            <ContainerJourneyCardChip
              label={`${t('vgm', 'VGM')}: ${orderData?.vgm.toLocaleString(undefined, { maximumFractionDigits: 0 })}kg`}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  )
})
